import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';

const sanitizeInput = (input) => DOMPurify.sanitize(input);

function RegistrationForm() {
  const [formData, setFormData] = useState({
    firmenname: '',
    adresse: '',
    kontaktperson: '',
    email: '',
    telefon: '',
    passwort: '',
    status: 'offen',
  });

  const [errorMessages, setErrorMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (value.length > 255) return;
    setFormData({ ...formData, [name]: sanitizeInput(value) });
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const validateEmail = (email) =>
    /^[A-Za-z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|ch)$/.test(email);

  const validatePassword = (pw) => pw.length >= 5;
  const validatePhoneNumber = (phone) =>
    /^(\+41|\+49)\d{9,}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessages([]);

    const errors = [];
    if (!formData.firmenname.trim()) errors.push('Bitte geben Sie einen Firmennamen ein.');
    if (!formData.adresse.trim()) errors.push('Bitte geben Sie eine Adresse ein.');
    if (!formData.kontaktperson.trim()) errors.push('Bitte geben Sie eine Kontaktperson ein.');
    if (!validateEmail(formData.email)) errors.push('Bitte geben Sie eine gültige E-Mail-Adresse ein (nur .com oder .ch erlaubt).');
    if (!validatePhoneNumber(formData.telefon)) errors.push('Bitte geben Sie eine gültige Telefonnummer ein (Schweiz oder Deutschland mit Ländervorwahl).');
    if (!validatePassword(formData.passwort)) errors.push('Das Passwort muss mindestens 5 Zeichen lang sein.');

    if (errors.length > 0) {
      setErrorMessages(errors);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://company-application-platform-backend.onrender.com/api/firma/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          status: 'offen'
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Fehler bei der Registrierung');
      }

      navigate('/registration-success');
    } catch (error) {
      console.error('Fehler bei der Registrierung:', error);
      setErrorMessages([error.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="py-12">
        <div>
          <div className="registration-container">
            <div className="registration-card">
              <div className="registration-header">
                <h1 className="registration-title">Firma registrieren</h1>
                <p className="registration-subtitle">
                  Füllen Sie das Formular aus, um Ihre Firma zu registrieren und den Bewerbungsprozess zu starten.
                </p>
              </div>

              {errorMessages.length > 0 && (
                <div className="error-alert">
                  <div className="error-alert-icon">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="error-alert-content">
                    <h3 className="error-alert-title">Bitte überprüfen Sie Ihre Eingaben</h3>
                    <ul className="error-alert-list">
                      {errorMessages.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <form className="registration-form" onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-section">
                    <div className="input-group">
                      <label htmlFor="firmenname" className="input-label">
                        Firmenname *
                      </label>
                      <div className={`input-wrapper ${focusedField === 'firmenname' ? 'focused' : ''} ${formData.firmenname ? 'has-value' : ''}`}>
                        <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <input
                          id="firmenname"
                          name="firmenname"
                          type="text"
                          required
                          value={formData.firmenname}
                          onChange={handleChange}
                          onFocus={() => handleFocus('firmenname')}
                          onBlur={handleBlur}
                          className="form-input"
                          placeholder="Ihre Firma AG"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="input-group">
                      <label htmlFor="adresse" className="input-label">
                        Adresse *
                      </label>
                      <div className={`input-wrapper ${focusedField === 'adresse' ? 'focused' : ''} ${formData.adresse ? 'has-value' : ''}`}>
                        <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <input
                          id="adresse"
                          name="adresse"
                          type="text"
                          required
                          value={formData.adresse}
                          onChange={handleChange}
                          onFocus={() => handleFocus('adresse')}
                          onBlur={handleBlur}
                          className="form-input"
                          placeholder="Musterstraße 123, 1234 Stadt"
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="input-group">
                      <label htmlFor="kontaktperson" className="input-label">
                        Kontaktperson *
                      </label>
                      <div className={`input-wrapper ${focusedField === 'kontaktperson' ? 'focused' : ''} ${formData.kontaktperson ? 'has-value' : ''}`}>
                        <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <input
                          id="kontaktperson"
                          name="kontaktperson"
                          type="text"
                          required
                          value={formData.kontaktperson}
                          onChange={handleChange}
                          onFocus={() => handleFocus('kontaktperson')}
                          onBlur={handleBlur}
                          className="form-input"
                          placeholder="Max Mustermann"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="input-group">
                      <label htmlFor="email" className="input-label">
                        E-Mail *
                      </label>
                      <div className={`input-wrapper ${focusedField === 'email' ? 'focused' : ''} ${formData.email ? 'has-value' : ''}`}>
                        <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => handleFocus('email')}
                          onBlur={handleBlur}
                          className="form-input"
                          placeholder="kontakt@firma.ch"
                          disabled={loading}
                        />
                      </div>
                      <p className="input-hint">Nur .com oder .ch E-Mail-Adressen erlaubt</p>
                    </div>

                    <div className="input-group">
                      <label htmlFor="telefon" className="input-label">
                        Telefon *
                      </label>
                      <div className={`input-wrapper ${focusedField === 'telefon' ? 'focused' : ''} ${formData.telefon ? 'has-value' : ''}`}>
                        <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <input
                          id="telefon"
                          name="telefon"
                          type="tel"
                          required
                          value={formData.telefon}
                          onChange={handleChange}
                          onFocus={() => handleFocus('telefon')}
                          onBlur={handleBlur}
                          className="form-input"
                          placeholder="+41 79 123 45 67"
                          disabled={loading}
                        />
                      </div>
                      <p className="input-hint">Schweiz oder Deutschland mit Ländervorwahl (+41 oder +49)</p>
                    </div>

                    <div className="input-group">
                      <label htmlFor="passwort" className="input-label">
                        Passwort *
                      </label>
                      <div className={`input-wrapper ${focusedField === 'passwort' ? 'focused' : ''} ${formData.passwort ? 'has-value' : ''}`}>
                        <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <input
                          id="passwort"
                          name="passwort"
                          type={showPassword ? "text" : "password"}
                          required
                          value={formData.passwort}
                          onChange={handleChange}
                          onFocus={() => handleFocus('passwort')}
                          onBlur={handleBlur}
                          className="form-input"
                          placeholder="Mindestens 5 Zeichen"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                        >
                          {showPassword ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <p className="input-hint">Mindestens 5 Zeichen</p>
                    </div>
                  </div>
                </div>

                <div className="checkbox-group">
                  <div className="custom-checkbox">
                    <input
                      id="agb"
                      name="agb"
                      type="checkbox"
                      required
                      className="checkbox-input"
                      disabled={loading}
                    />
                    <span className="checkbox-mark"></span>
                    <label htmlFor="agb" className="checkbox-label">
                      Ich stimme den <Link to="/datenschutz" className="checkbox-link">Datenschutzbestimmungen</Link> zu *
                    </label>
                  </div>
                </div>

                <div className="form-footer">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`submit-button ${loading ? 'loading' : ''}`}
                  >
                    {loading ? (
                      <>
                        <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Wird verarbeitet...
                      </>
                    ) : 'Jetzt registrieren'}
                  </button>
                </div>
              </form>

              <div className="form-footer-links">
                <p className="footer-text">
                  Bereits registriert?{' '}
                  <Link to="/firmen-list" className="footer-link">
                    Zur Firmenliste
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default RegistrationForm;